import React, { useState } from 'react';

/**
 * How the agent actually starts talking through the gateway.
 *
 * <h3>Why the path decides what is shown</h3>
 *
 * An agent whose source the customer owns takes the SDK decorator; an
 * off-the-shelf one is pointed at the MCP gateway and changes no code. Showing
 * both would ask somebody to work out which of two sets of instructions applies
 * to them, on the one screen where getting it wrong means nothing is governed.
 */
export default function SnippetAsk({ snippets, path = 'CODED', onAnswer, busy = false }) {
  const [language, setLanguage] = useState('java');
  const [copied, setCopied] = useState(false);
  const mcp = String(path).toUpperCase() === 'MCP';

  const body = mcp
    ? snippets?.mcpConfig
    : (language === 'java' ? snippets?.java : snippets?.python);

  const copy = async () => {
    try { await navigator.clipboard.writeText(body || ''); setCopied(true); } catch { setCopied(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' }}>
      <p style={NOTE}>
        {mcp
          ? 'Point the agent at this gateway with the key you just saved. Its own code does not '
            + 'change.'
          : 'Wrap its entry point with the Agentlets decorator. Every call is then protected, '
            + 'with the request and the response both on the record.'}
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {mcp ? null : ['java', 'python'].map((l) => (
          <button key={l} type="button" aria-pressed={language === l}
                  className={language === l ? 'p7-btn p7-btn--sm p7-btn--primary' : 'p7-btn p7-btn--sm'}
                  onClick={() => setLanguage(l)}>
            {l === 'java' ? 'Java' : 'Python'}
          </button>
        ))}
        <button type="button" className="p7-btn p7-btn--sm" style={{ marginLeft: 'auto' }}
                onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      <pre className="p7-mono"
           style={{ margin: 0, padding: '12px 13px', overflowX: 'auto',
                    background: 'var(--p7-surface-sunken)', border: '1px solid var(--p7-line)',
                    borderRadius: 'var(--p7-radius-control)', fontSize: '12px',
                    lineHeight: 1.6, color: 'var(--p7-ink)' }}>
        {body || 'No snippet came back for this path.'}
      </pre>

      <div>
        <button type="button" className="p7-btn p7-btn--primary" disabled={busy}
                onClick={() => onAnswer({ language: mcp ? 'mcp' : language })}>
          Done
        </button>
      </div>
    </div>
  );
}

const NOTE = {
  margin: 0, font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
  color: 'var(--p7-ink-subtle)', lineHeight: 1.5,
};
